import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '@/firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { acceptInvitation, getInvitationByToken, processInvitationForExistingUser, processPendingInvitationsForUser, type Invitation } from '@/services/invitationService';
import { userService } from '@/firebase/database';
import { Eye, EyeOff, AlertCircle, CheckCircle, X } from 'lucide-react';
import { redirectToMainDomain } from '@/utils/domainUtils';
import { trackAuth } from '@/utils/analytics';
import { findOrganizationBySSODomain } from '@/services/organizationSSOService';
import {
  resolveLoginIdentifier,
  formatLoginIdentifierForDisplay,
  isSyntheticStaffAuthEmail,
} from '@/utils/staffUsernameAuth';
import { getUsersDocument } from '@/firebase/getUsersDocument';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const { login, isAuthenticated, isLoading } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      redirectToMainDomain('/home');
    }
  }, [isAuthenticated, isLoading]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [name, setName] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [checkingSSO, setCheckingSSO] = useState(false);
  const [ssoOrganization, setSsoOrganization] = useState<any>(null);

  useEffect(() => {
    const token = searchParams.get('invitation');
    console.log('🔍 LoginPage useEffect - invitation token from URL:', token);
    if (token) {
      setInvitationToken(token);
      console.log('✅ Invitation token set in state:', token);
      // Validate invitation and load details
      getInvitationByToken(token).then(async result => {
        if (result.success && result.invitation) {
          console.log('✅ Invitation validated:', result.invitation);
          setInvitation(result.invitation);
          setEmail(formatLoginIdentifierForDisplay(result.invitation.email));
          
          // Check if invitee already has an account; if not, show create-account form directly
          let showSignUp = false;
          try {
            const functions = getFunctions();
            const checkRecipient = httpsCallable<{ token: string }, { valid: boolean; userExists: boolean }>(functions, 'checkInvitationRecipientExists');
            const checkResult = await checkRecipient({ token });
            const data = checkResult.data;
            if (data?.valid && !data?.userExists) {
              showSignUp = true;
              console.log('✅ Invitee has no account yet – showing Create Account');
            }
          } catch (err) {
            console.warn('Could not check if invitee has account, defaulting to sign-in:', err);
          }
          setIsSignUpMode(showSignUp);
        } else {
          console.warn('⚠️ Failed to validate invitation:', result.error);
        }
      });
    } else {
      console.log('ℹ️ No invitation token in URL');
    }
    
    // Check if signup parameter is present - show signup form directly
    const signupParam = searchParams.get('signup');
    if (signupParam === 'true') {
      setIsSignUpMode(true);
    }
    
    // Check if password reset link was clicked (from error message)
    const resetParam = searchParams.get('reset');
    const emailParam = searchParams.get('email');
    if (resetParam === 'true' && emailParam) {
      setShowForgotPassword(true);
      setForgotPasswordEmail(emailParam);
    }
  }, [searchParams]);

  // Check for SSO when email changes (debounced)
  useEffect(() => {
    const checkSSO = async () => {
      if (!email || email.indexOf('@') === -1) {
        setSsoOrganization(null);
        return;
      }

      try {
        const org = await findOrganizationBySSODomain(email);
        setSsoOrganization(org);
      } catch (error) {
        console.error('Error checking SSO:', error);
        setSsoOrganization(null);
      }
    };

    // Debounce SSO check
    const timeoutId = setTimeout(checkSSO, 500);
    return () => clearTimeout(timeoutId);
  }, [email]);

  const handleSSOLogin = async () => {
    if (!ssoOrganization) {
      return;
    }

    setCheckingSSO(true);
    setError('');

    try {
      // Store email and org ID in sessionStorage for callback page
      sessionStorage.setItem('sso_email', email);
      sessionStorage.setItem('sso_org_id', ssoOrganization.id);
      
      // Navigate to SSO callback page which will initiate the redirect
      // This ensures Firebase Auth redirects back to /sso-callback, not /login
      navigate('/sso-callback?initiate=true');
    } catch (error: any) {
      console.error('SSO login error:', error);
      setError(`SSO login failed: ${error.message}`);
      setCheckingSSO(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both username (or email) and password');
      return;
    }

    const resolved = resolveLoginIdentifier(email);
    if (resolved.error || !resolved.authEmail) {
      setError(resolved.error || 'Invalid username or email');
      return;
    }
    const authEmail = resolved.authEmail;

    // Always allow email/password login for backward compatibility
    // SSO is optional and only triggered by the "Sign in with SSO" button
    if (isSignUpMode) {
      // Create account (either for invitation or self-signup)
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (password.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }

      if (!name.trim() && !invitationToken) {
        setError('Please enter your name');
        return;
      }

      setLoading(true);
      setError('');

      try {
        let firebaseUser;
        
        if (invitationToken) {
          // Create account via invitation
          const result = await acceptInvitation(invitationToken, password);
          if (!result.success) {
            // Check if this is the special case where account exists and password is wrong
            if (result.error === 'ACCOUNT_EXISTS_NEEDS_RESET') {
              const resetEmail = (result as any).email || authEmail;
              setError('An account with this email already exists. Please use the correct password, or use the password reset form below to request a new password.');
              setShowForgotPassword(true);
              setForgotPasswordEmail(resetEmail);
              setLoading(false);
              return;
            }
            setError(result.error || 'Failed to create account');
            setLoading(false);
            return;
          }
          firebaseUser = auth.currentUser;
        } else {
          // Self-signup: Create new account
          const userCredential = await createUserWithEmailAndPassword(auth, authEmail, password);
          firebaseUser = userCredential.user;
          
          // Update Firebase Auth profile
          await updateProfile(firebaseUser, {
            displayName: name.trim()
          });

          // Send email verification using custom SMTP
          try {
            const functions = getFunctions();
            const sendCustomEmailVerification = httpsCallable(functions, 'sendCustomEmailVerification');
            console.log('📧 Calling sendCustomEmailVerification with:', {
              userId: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: name.trim()
            });
            const result = await sendCustomEmailVerification({
              userId: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: name.trim()
            });
            console.log('✅ Verification email sent successfully:', result);
          } catch (verificationError: any) {
            console.error('⚠️ Failed to send verification email:', verificationError);
            console.error('Error details:', {
              code: verificationError.code,
              message: verificationError.message,
              details: verificationError.details
            });
            // Don't block signup if verification email fails - just log it
            // NOTE: If this fails, user won't get verification email from custom SMTP
            // They may need to use resend verification feature
          }

          // Create user document in Firestore
          await userService.createUser(firebaseUser.uid, {
            id: firebaseUser.uid,
            name: name.trim(),
            email: firebaseUser.email,
            role: 'admin', // New signups become admin (they'll create their own org)
            createdAt: new Date(),
            updatedAt: new Date(),
            authProvider: 'email'
          });
        }

        if (firebaseUser) {
          // Get user data from Firestore
          const userDoc = await getUsersDocument(firebaseUser.uid);
          const userData = userDoc.exists() ? userDoc.data() : {};
          
          // For invited users, use Firestore verified_email flag (since invitation already verified email)
          // For regular users, use Firebase Auth emailVerified status
          const isVerified = userData?.verified_email !== undefined 
            ? userData.verified_email 
            : firebaseUser.emailVerified;
          
          const user = {
            id: firebaseUser.uid,
            name: firebaseUser.displayName || userData?.name || name.trim() || invitation?.name || '',
            email: firebaseUser.email || '',
            picture: firebaseUser.photoURL || '',
            verified_email: isVerified,
            organizationId: userData?.organizationId,
            role: userData?.role,
          };
          
          await login(user);
          
          // Track signup
          trackAuth.signup('email');
          
          // Small delay to ensure state is updated before redirect
          setTimeout(() => {
            // If email is not verified (and user wasn't invited), redirect to verification page
            if (!isVerified) {
              redirectToMainDomain('/verify-email');
            } else if (!userData?.organizationId && !invitationToken) {
              // If user doesn't have an organization, redirect to create one
              redirectToMainDomain('/organizations?newSignup=true');
            } else {
              redirectToMainDomain('/home');
            }
          }, 100);
        }
      } catch (error: any) {
        console.error('Account creation error:', error);
        if (error.code === 'auth/email-already-in-use') {
          setError('An account with this email already exists. Please sign in instead.');
          setIsSignUpMode(false);
        } else if (error.code === 'auth/weak-password') {
          setError('Password is too weak. Please choose a stronger password.');
        } else if (
          typeof error?.message === 'string' &&
          error.message.includes('offline')
        ) {
          setError(
            'Cannot reach the database (offline). Check your connection, try clearing this site’s stored data, then try again.'
          );
        } else {
          setError(error.message || 'Failed to create account. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    } else {
      // Regular login
      setLoading(true);
      setError('');

      try {
        console.log('🔐 Attempting to sign in user:', authEmail);
        console.log('🔍 Invitation token in state:', invitationToken);
        
        // If there's an invitation token, ALWAYS use acceptInvitation (it handles both new and existing accounts)
        if (invitationToken) {
          console.log('🎯 Invitation token detected - using acceptInvitation flow');
          const result = await acceptInvitation(invitationToken, password);
          
          if (!result.success) {
            // Check if this is the special case where account exists and password is wrong
            if (result.error === 'ACCOUNT_EXISTS_NEEDS_RESET') {
              const resetEmail = (result as any).email || authEmail;
              setError('An account with this email already exists. Please use the correct password, or use the password reset form below to request a new password.');
              setShowForgotPassword(true);
              setForgotPasswordEmail(resetEmail);
              setLoading(false);
              return;
            }
            setError(result.error || 'Failed to accept invitation');
            setLoading(false);
            return;
          }
          
          // Accept invitation succeeded - get the user
          const firebaseUser = auth.currentUser;
          if (!firebaseUser) {
            setError('User account created but authentication failed. Please try logging in again.');
            setLoading(false);
            return;
          }
          
          console.log('✅ Invitation accepted, user authenticated:', firebaseUser.uid);
          
          // Get user data from Firestore (should exist now after acceptInvitation)
          const userDoc = await getUsersDocument(firebaseUser.uid);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Track login
            trackAuth.login('email');
            console.log('📋 User data from Firestore:', { 
              organizationId: userData.organizationId, 
              hasOrganizationIds: !!userData.organizationIds 
            });
            
            // For invited users, use Firestore verified_email flag (since invitation already verified email)
            const isVerified = userData?.verified_email !== undefined 
              ? userData.verified_email 
              : firebaseUser.emailVerified;
            
            // Create user object for our context
            const user = {
              id: firebaseUser.uid,
              name: firebaseUser.displayName || userData.name || '',
              email: firebaseUser.email || '',
              picture: firebaseUser.photoURL || '',
              verified_email: isVerified,
            };
            
            // Login the user (for multi-org use first org so GLBA session can be created)
            const loginOrgId = userData?.organizationId ?? userData?.organizationIds?.[0];
            await login(user, loginOrgId);
            
            // Determine redirect path
            let redirectPath = '/home';
            if (!isVerified) {
              redirectPath = '/verify-email';
            } else if (!userData?.organizationId) {
              redirectPath = '/organizations?newSignup=true';
            }
            
            // Redirect
            setTimeout(() => {
              redirectToMainDomain(redirectPath);
            }, 100);
          } else {
            console.error('❌ ERROR: User document not found after acceptInvitation');
            setError('Account created but user data not found. Please contact support.');
            setLoading(false);
          }
          return;
        }
        
        // No invitation token - proceed with normal sign-in
        const userCredential = await signInWithEmailAndPassword(auth, authEmail, password);
        const firebaseUser = userCredential.user;
        console.log('✅ User signed in successfully:', firebaseUser.uid);
        
        // Get user data from Firestore
        const userDoc = await getUsersDocument(firebaseUser.uid);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Track login
          trackAuth.login('email');
          console.log('📋 User data from Firestore:', { 
            organizationId: userData.organizationId, 
            hasOrganizationIds: !!userData.organizationIds 
          });
          
          // For invited users, use Firestore verified_email flag (since invitation already verified email)
          // For regular users, use Firebase Auth emailVerified status
          const isVerified = userData?.verified_email !== undefined 
            ? userData.verified_email 
            : firebaseUser.emailVerified;
          
          // Create user object for our context
          const user = {
            id: firebaseUser.uid,
            name: firebaseUser.displayName || userData.name || '',
            email: firebaseUser.email || '',
            picture: firebaseUser.photoURL || '',
            verified_email: isVerified,
          };
          
          // Process invitations BEFORE calling login() to avoid redirect race condition
          let shouldRedirect = true;
          let redirectPath = '/home';
          
          // Check for pending invitations (invitation token already handled above in acceptInvitation flow)
          if (!invitationToken) {
            // No invitation token - check for pending invitations
            console.log('ℹ️ No invitation token, checking for pending invitations...');
            try {
              console.log('🔍 Calling processPendingInvitationsForUser for:', { userId: firebaseUser.uid, email: firebaseUser.email });
              const pendingResult = await processPendingInvitationsForUser(firebaseUser.uid, firebaseUser.email || '');
              console.log('📋 processPendingInvitationsForUser result:', pendingResult);
              if (pendingResult.success && pendingResult.processedCount && pendingResult.processedCount > 0) {
                console.log(`✅ Processed ${pendingResult.processedCount} pending invitation(s)`);
                // Don't redirect immediately - let the page reload to show new organizations
                shouldRedirect = false;
                setTimeout(() => {
                  window.location.reload();
                }, 500);
              } else {
                console.log('ℹ️ No pending invitations to process');
              }
            } catch (pendingError) {
              console.error('❌ Error processing pending invitations:', pendingError);
            }
          }
          
          // Now login the user (for multi-org use first org so GLBA session can be created)
          const loginOrgId = userData?.organizationId ?? userData?.organizationIds?.[0];
          await login(user, loginOrgId);
          
          // Determine redirect path
          // For invited users, use Firestore verified_email; for others, use Firebase Auth emailVerified
          if (!isVerified) {
            redirectPath = '/verify-email';
          } else if (!userData?.organizationId && !invitationToken) {
            redirectPath = '/organizations?newSignup=true';
          }
          
          // Redirect if needed
          if (shouldRedirect) {
            setTimeout(() => {
              redirectToMainDomain(redirectPath);
          }, 100);
          }
        } else {
          setError('User account not found. Please contact your administrator.');
        }
        
      } catch (error: any) {
        console.error('Login error:', error);
        
        if (error.code === 'auth/user-not-found') {
          // If we have an invitation, suggest creating account
          if (invitationToken) {
            setError('No account found for this invitation. Please create an account using the form below.');
            setIsSignUpMode(true);
          } else {
            setError('No account found with this username or email address');
          }
        } else if (error.code === 'auth/wrong-password') {
          setError('Incorrect password');
        } else if (error.code === 'auth/invalid-email') {
          setError('Invalid username or email address');
        } else if (error.code === 'auth/too-many-requests') {
          setError('Too many failed attempts. Please try again later');
        } else if (
          typeof error?.message === 'string' &&
          error.message.includes('offline')
        ) {
          setError(
            'Cannot reach the database (offline). Check your internet connection, try another network or VPN off, or clear this site’s stored data (browser settings → Site data) and sign in again.'
          );
        } else {
          setError('Login failed. Please try again');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      setForgotPasswordMessage('Please enter your username or email address');
      return;
    }

    const resolved = resolveLoginIdentifier(forgotPasswordEmail);
    if (resolved.error || !resolved.authEmail) {
      setForgotPasswordMessage(resolved.error || 'Invalid username or email');
      return;
    }

    if (isSyntheticStaffAuthEmail(resolved.authEmail)) {
      setForgotPasswordMessage(
        'Password reset email is not available for staff usernames. Ask an admin or manager to set a new password for your account in User management (Set password).'
      );
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');

    try {
      await sendPasswordResetEmail(auth, resolved.authEmail);
      setForgotPasswordMessage('Password reset email sent! Check your inbox and follow the instructions to reset your password.');
    } catch (error: any) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        setForgotPasswordMessage('No account found with this username or email address');
      } else if (error.code === 'auth/invalid-email') {
        setForgotPasswordMessage('Invalid username or email address');
      } else {
        setForgotPasswordMessage('Failed to send reset email. Please try again.');
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Complin Logo */}
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="w-5 h-5 rounded-full bg-black flex-shrink-0"></div>
            <span className="text-xl font-bold text-gray-900 lowercase" style={{ letterSpacing: '-0.5px', fontFamily: 'Inter, sans-serif' }}>complin</span>
          </div>
          {isSignUpMode && (
            <>
          <h1 className="text-3xl font-bold text-gray-900">
                {invitationToken ? 'Create Account' : 'Sign Up'}
          </h1>
          <p className="mt-2 text-gray-600">
                {invitationToken
                ? 'Create your account to accept the invitation'
                  : 'Create your account to get started'}
          </p>
            </>
          )}
        </div>

        {invitation && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Accepting Invitation</p>
                <p className="text-xs text-blue-700 mt-1">
                  You're accepting an invitation for{' '}
                  <span className="font-medium">{formatLoginIdentifierForDisplay(invitation.email)}</span> as{' '}
                  {invitation.role}
                </p>
                <p className="text-xs text-blue-600 mt-2 font-medium">
                  {isSignUpMode 
                    ? "Create your account below to accept the invitation. Already have an account? Switch to sign in."
                    : "Sign in with your existing password to accept the invitation. Don't have an account? Switch to sign up to create one."}
                </p>
              </div>
            </div>
          </div>
        )}

        {ssoOrganization && ssoOrganization.sso?.enabled && !isSignUpMode && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">Single Sign-On Available</p>
                <p className="text-xs text-green-700 mt-1">
                  Your organization ({ssoOrganization.name}) uses Single Sign-On. Click the button below to sign in with your organization account.
                </p>
                <button
                  type="button"
                  onClick={handleSSOLogin}
                  disabled={checkingSSO}
                  className="mt-3 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkingSSO ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Redirecting to SSO...
                    </div>
                  ) : (
                    `Sign in with ${ssoOrganization.sso.provider === 'azure-ad' ? 'Microsoft 360' : 'SSO'}`
                  )}
                </button>
                <p className="text-xs text-green-600 mt-2 text-center">
                  Or continue with username (or email) and password below
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 space-y-6">
          {/* Email/Password Form */}
          <form className="mt-8 space-y-6" onSubmit={handleEmailLogin}>
            <div className="space-y-4">
              {isSignUpMode && !invitationToken && (
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Username or email
                </label>
                <input
                  id="email"
                  name="email"
                  type="text"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="e.g. downtown-housekeeping or you@company.com"
                  disabled={!!invitationToken}
                />
                {!invitationToken && (
                  <p className="mt-1 text-xs text-gray-500">
                    Shared staff accounts: pick a unique username for your property, e.g.{' '}
                    <span className="font-mono text-gray-700">yourpropertycode-housekeeping</span> (letters, numbers,
                    hyphens; 3–64 characters).
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={isSignUpMode ? 'new-password' : 'current-password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm pr-10"
                    placeholder={isSignUpMode ? 'Create a password' : 'Enter your password'}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {isSignUpMode && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm pr-10"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isSignUpMode ? 'Creating Account...' : 'Signing In...'}
                </div>
              ) : (
                isSignUpMode ? 'Create Account' : 'Sign In'
              )}
              </button>
            </div>
          </form>

          {/* Additional Links */}
          {invitationToken ? (
            <div className="text-center space-y-2">
              {!isSignUpMode ? (
                <>
                  <p className="text-sm text-gray-600">
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setIsSignUpMode(true);
                        setError('');
                      }}
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Create account
                    </button>
                  </p>
                  <p className="text-xs text-gray-500">
                    Forgot your password?{' '}
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Reset it here
                    </button>
                  </p>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setIsSignUpMode(false);
                    setError('');
                    setName('');
                    setConfirmPassword('');
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Already have an account? Sign in with your existing password
                </button>
              )}
            </div>
          ) : (
            <div className="text-center space-y-2">
              {!isSignUpMode ? (
                <>
                  <p className="text-sm text-gray-600">
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setIsSignUpMode(true)}
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Sign up for free
                    </button>
                  </p>
                  <p className="text-xs text-gray-500">
                    Forgot your password?{' '}
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Reset it here
                    </button>
                  </p>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setIsSignUpMode(false);
                    setError('');
                    setName('');
                    setConfirmPassword('');
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Already have an account? Sign in
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Reset Password</h3>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordMessage('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label htmlFor="forgotEmail" className="block text-sm font-medium text-gray-700">
                    Username or email
                  </label>
                  <input
                    id="forgotEmail"
                    name="forgotEmail"
                    type="text"
                    autoComplete="username"
                    required
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Your work email or staff username"
                  />
                </div>

                {forgotPasswordMessage && (
                  <div className={`p-3 rounded-md text-sm ${
                    forgotPasswordMessage.includes('sent') 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {forgotPasswordMessage}
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setForgotPasswordMessage('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {forgotPasswordLoading ? 'Sending...' : 'Send Reset Email'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;